import { useState, type ReactNode } from 'react';
import { Bot, Cloud } from 'lucide-react';
import { Button, Card, Pill } from '../../components/ui';
import { CollapsibleGroup, useDetailsItem } from '../../components/ui/CollapsibleGroup';

/**
 * Wiring an AI assistant to this platform.
 *
 * One way in, shown here: **Hosted** — this API answering MCP at `/mcp`. Paste
 * a URL and a key and be done — nothing downloaded, nothing built, no path to
 * get wrong, and no copy of the server going stale on somebody's laptop. The
 * stdio server in `apps/mcp` still exists (the same `buildTools` list over a
 * different pipe), but its "Run it locally instead" section came off this page
 * on 2026-09-26 at the operator's word: the page offers the one path people
 * should take, and a checkout-and-build path beside it was a second thing to
 * get wrong.
 *
 * Every value in every snippet is this install's own: the origin from the
 * browser (this page is served from the address the assistant needs, so it
 * cannot be stale).
 *
 * The clients are accordions with every row collapsed, and ONE open at a time
 * (the platform's rule, operator 2026-09-13): a dozen configuration blocks
 * stacked open is a card nobody scrolls past. Native `<details>` rather than
 * a state machine — keyboard-reachable, and findable by the browser's own
 * in-page search, for free — steered by `CollapsibleGroup`.
 */

const KEY_PLACEHOLDER = 'sak_your_key_here';

interface ClientSetup {
  id: string;
  label: string;
  /** Where the snippet goes — the file, or the command line. */
  where: string;
  snippet: string;
  /** Anything true about this client the snippet cannot say itself. */
  note?: string;
}

/**
 * The hosted shapes. Every one is a URL and a header — which is the point:
 * there is no path, no interpreter and no build in any of them.
 */
function hostedClients(url: string): ClientSetup[] {
  const json = (key: 'mcpServers' | 'servers', extra?: string) => `{
  "${key}": {
    "simpleacs": {${extra ?? ''}
      "url": "${url}",
      "headers": {
        "Authorization": "Bearer ${KEY_PLACEHOLDER}"
      }
    }
  }
}`;

  return [
    {
      id: 'h-claude-code',
      label: 'Claude Code',
      where: 'One command, from a terminal',
      snippet: `claude mcp add --transport http simpleacs ${url} \\
  --header "Authorization: Bearer ${KEY_PLACEHOLDER}"`,
    },
    {
      id: 'h-claude-desktop',
      label: 'Claude Desktop',
      where: 'claude_desktop_config.json',
      snippet: json('mcpServers'),
    },
    {
      id: 'h-cursor',
      label: 'Cursor',
      where: '.cursor/mcp.json in the project, or ~/.cursor/mcp.json for every project',
      snippet: json('mcpServers'),
    },
    {
      id: 'h-vscode',
      label: 'VS Code — Copilot agent mode',
      where: '.vscode/mcp.json',
      snippet: json('servers', '\n      "type": "http",'),
      note: 'VS Code names the block "servers", not "mcpServers", and wants the transport spelled out — the one shape here not interchangeable with its neighbours.',
    },
    {
      id: 'h-windsurf',
      label: 'Windsurf',
      where: '~/.codeium/windsurf/mcp_config.json',
      snippet: json('mcpServers'),
    },
    {
      id: 'h-kilo',
      label: 'Kilo Code',
      where: 'Its MCP Servers panel opens the settings file — or .kilocode/mcp.json in a project',
      snippet: json('mcpServers'),
      note: 'Same shape as Cline and Roo, which it shares a lineage with.',
    },
    {
      id: 'h-cline',
      label: 'Cline',
      where: 'cline_mcp_settings.json — the extension opens it from its MCP Servers panel',
      snippet: json('mcpServers'),
    },
    {
      id: 'h-other',
      label: 'Zed, Reasonix, Continue, JetBrains — anything else',
      where: 'Wherever your client keeps its MCP servers',
      snippet: `transport   streamable http
url         ${url}
header      Authorization: Bearer ${KEY_PLACEHOLDER}`,
      note: 'These three values are the whole contract for a hosted server. Deliberately not written as a file for these: their formats move faster than this page does, and a snippet that looks right and is wrong costs more than none. Check your client’s own MCP documentation for where they go.',
    },
  ];
}

/**
 * One client's snippet behind a fold. A native `<details>` still — keyboard-
 * reachable, findable by the browser's in-page search — but controlled through
 * the group above it, so opening one client shuts the other (the platform's
 * one-open rule, operator 2026-09-13).
 */
function ClientRow({
  client,
  copied,
  onCopy,
}: {
  client: ClientSetup;
  copied: boolean;
  onCopy: (what: string, text: string) => void;
}) {
  const { open, onToggle } = useDetailsItem(client.id);
  return (
    <details className="group" open={open} onToggle={onToggle}>
      <summary className="flex cursor-pointer select-none items-center justify-between gap-3 px-3 py-2.5 text-sm text-ink hover:bg-surface-2">
        <span className="font-medium">{client.label}</span>
        <span className="text-xs text-faint group-open:hidden">Show</span>
        <span className="hidden text-xs text-faint group-open:inline">Hide</span>
      </summary>
      <div className="space-y-2 border-t border-line px-3 py-3">
        <p className="text-xs text-muted">{client.where}</p>
        <pre className="overflow-x-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs text-ink">
          {client.snippet}
        </pre>
        {client.note && <p className="text-xs text-faint">{client.note}</p>}
        <Button size="sm" variant="secondary" onClick={() => onCopy(client.id, client.snippet)}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </details>
  );
}

/**
 * The Hosted half of the card (the only one since 2026-09-26). Module-level for the same reason as
 * `ClientAccordion` below — and the reason the first hoist fixed nothing: it
 * was still an arrow defined inside `McpCard`'s render, so every `setCopied`
 * (the Copy click, and the 2.5 s reset) handed React a NEW element type
 * here, and React unmounted the whole half — the accordion, its group and
 * its open key — and mounted a fresh, folded one. Nothing rendered under a
 * component that is recreated per render survives that parent's state
 * changes, however far down it is hoisted (review, 2026-09-13).
 */
function Section({
  icon,
  title,
  pill,
  blurb,
  children,
}: {
  icon: ReactNode;
  title: string;
  pill?: ReactNode;
  blurb: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <p className="flex items-center gap-2 text-sm font-medium text-ink">
        {icon}
        {title}
        {pill}
      </p>
      <div className="text-xs text-muted">{blurb}</div>
      {children}
    </div>
  );
}

/**
 * One list of clients, one open at a time. Module-level on purpose: defined
 * inside the card's render it was a new component type every render, so a
 * Copy click (which sets `copied`) remounted the list and folded the row the
 * operator had just opened.
 */
function ClientAccordion({
  clients,
  copied,
  onCopy,
}: {
  clients: ClientSetup[];
  copied: string | null;
  onCopy: (what: string, text: string) => void;
}) {
  return (
    <div className="divide-y divide-line rounded-lg border border-line">
      <CollapsibleGroup>
        {clients.map((client) => (
          <ClientRow key={client.id} client={client} copied={copied === client.id} onCopy={onCopy} />
        ))}
      </CollapsibleGroup>
    </div>
  );
}

export function McpCard() {
  const [copied, setCopied] = useState<string | null>(null);
  const origin = window.location.origin;
  const url = `${origin}/mcp`;

  const copy = async (what: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied(null), 2500);
  };

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Bot className="size-4 text-muted" />
          AI assistant access (MCP)
        </span>
      }
    >
      <div className="space-y-5 p-5">
        <p className="text-sm text-muted">
          An assistant can work this platform through the Model Context Protocol — asking why a
          subscriber is down, which ONUs have degraded optics, how many a bulk filter would
          touch. It authenticates with a key you mint above, so every capability check, org
          scope and audit line applies to it unchanged.
        </p>

        <div className="rounded-lg border border-line bg-surface-2 p-3">
          <p className="text-xs font-medium text-ink">What it can do</p>
          <p className="mt-1 text-xs text-muted">
            Exactly what its key can do over the API — the same capability behind every tool,
            and the hosted endpoint offers a key only the tools it holds. Reads: the fleet, ONU
            search and detail, one CPE, stored chassis health, an OLT&rsquo;s ONU events with
            the cause the chassis reported, drift findings, the action log, the physical
            topology, billing status, and a count of what a bulk filter would match (the action
            log and drift findings need audit.read, which a read key does not carry). Writes:
            reboot, resync, recreate, swap, management mode, authorise, WiFi and preset on one
            ONU or CPE at a time — each a dry run by default and refused live without{' '}
            <code className="font-mono">confirm</code>; summon, which changes nothing, has neither;
            WAN has no dry run on its route, so it always runs live and always needs{' '}
            <code className="font-mono">confirm</code> — and, only for a key holding bulk.execute,
            the plan → typed confirmation → execute ladder.
          </p>
          <p className="mt-1 text-xs text-muted">
            The recommended key for an assistant that manages ONUs is a Custom key started from
            the <b>ONU management</b> preset above: read the fleet, act on one ONU or CPE at a
            time, never a bulk run.
          </p>
        </div>

        <Section
          icon={<Cloud className="size-4 text-muted" />}
          title="Hosted"
          pill={<Pill tone="success">nothing to install</Pill>}
          blurb={
            <>
              This platform answers MCP itself. Give the assistant the address below and a key —
              there is no download, no build, and no path to keep in step with an upgrade.
            </>
          }
        >
          <div className="space-y-2">
            <pre className="overflow-x-auto rounded-lg border border-line bg-surface-2 p-3 font-mono text-xs text-ink">
              {url}
            </pre>
            <Button size="sm" variant="secondary" onClick={() => copy('url', url)}>
              {copied === 'url' ? 'Copied' : 'Copy the address'}
            </Button>
          </div>
          <ClientAccordion clients={hostedClients(url)} copied={copied} onCopy={copy} />
        </Section>
      </div>
    </Card>
  );
}
